/**
 * MarkAsTransferModal Component
 *
 * Modal dialog for manually marking a transaction as a transfer.
 * Allows the user to select a destination account (asset or liability)
 * to classify the transaction as a transfer between accounts.
 *
 * Features:
 * - Transaction details display (description, date, amount)
 * - Destination account selection via AccountTreeSelect
 * - Filtered to asset and liability accounts only
 * - Loading state during API call
 * - Success/error toast notifications
 * - Keyboard shortcuts (Escape to close)
 */

import { useState, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { X, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { Transaction, Account } from '@/types/accounting';
import { AccountTreeSelect } from '../../shared/AccountTreeSelect';

export interface MarkAsTransferModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Transaction to mark as transfer */
  transaction: Transaction;
  /** Flat list of accounts */
  accounts: Account[];
  /** Hierarchical tree for account selection */
  accountTree: Account[];
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when transfer is marked successfully */
  onSuccess?: (transaction: Transaction) => void;
}

/**
 * Get CSRF token from meta tag
 */
function getCsrfToken(): string {
  return (
    document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ||
    ''
  );
}

/**
 * Format date for display (DD.MM.YYYY)
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * MarkAsTransferModal for manually marking transactions as transfers.
 *
 * @example
 * <MarkAsTransferModal
 *   isOpen={showModal}
 *   transaction={transaction}
 *   accounts={accounts}
 *   accountTree={accountTree}
 *   onClose={() => setShowModal(false)}
 *   onSuccess={(updatedTransaction) => {
 *     // Handle success
 *   }}
 * />
 */
export function MarkAsTransferModal({
  isOpen,
  transaction,
  accounts,
  accountTree,
  onClose,
  onSuccess,
}: MarkAsTransferModalProps) {
  const [destinationAccountId, setDestinationAccountId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Reset destination account when modal opens
  const handleOpen = useCallback(() => {
    if (isOpen) {
      setDestinationAccountId(null);
    }
  }, [isOpen]);

  // Effect to reset state when modal opens
  useState(() => {
    handleOpen();
  });

  // Check if submit button should be enabled
  const canSubmit = useMemo(() => {
    return destinationAccountId !== null && !isLoading;
  }, [destinationAccountId, isLoading]);

  // Handle form submission
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!canSubmit || destinationAccountId === null) return;

      setIsLoading(true);

      try {
        const response = await fetch(
          `/api/accounting/transactions/${transaction.id}/mark_as_transfer`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': getCsrfToken(),
            },
            body: JSON.stringify({
              destinationAccountId,
            }),
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to mark as transfer');
        }

        toast.success(data.message || 'Transaction marked as transfer');

        if (onSuccess) {
          onSuccess(data.transaction);
        }

        onClose();
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : 'Failed to mark as transfer'
        );
      } finally {
        setIsLoading(false);
      }
    },
    [canSubmit, destinationAccountId, transaction.id, onSuccess, onClose]
  );

  // Handle modal close
  const handleClose = useCallback(() => {
    if (!isLoading) {
      onClose();
    }
  }, [isLoading, onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose]
  );

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose]
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      data-testid="mark-as-transfer-modal-backdrop"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="mark-as-transfer-modal"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Mark as Transfer
          </h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className={clsx(
              'p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100',
              'focus:outline-none focus:ring-2 focus:ring-blue-500',
              isLoading && 'cursor-not-allowed opacity-50'
            )}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-4 space-y-4">
            {/* Transaction Details */}
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-md space-y-2">
              <div>
                <p className="text-sm font-medium text-gray-700">
                  {transaction.description}
                </p>
              </div>
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>{formatDate(transaction.date)}</span>
                <span className="font-semibold">{transaction.amountDisplay}</span>
              </div>
            </div>

            {/* Instructions */}
            <div className="text-sm text-gray-600">
              <p>
                Select the destination account to mark this transaction as a transfer.
                This will classify the transaction as a movement of money between two accounts.
              </p>
            </div>

            {/* Destination Account Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Destination Account <span className="text-red-500">*</span>
              </label>
              <AccountTreeSelect
                accounts={accounts}
                accountTree={accountTree}
                value={destinationAccountId}
                onChange={(id) => setDestinationAccountId(id)}
                placeholder="Select destination account..."
                filterTypes={['asset', 'liability']}
                disabled={isLoading}
                data-testid="destination-account-select"
              />
              <p className="mt-1 text-xs text-gray-500">
                Only asset and liability accounts can be transfer destinations
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className={clsx(
                'px-4 py-2 text-sm font-medium rounded-md',
                'text-gray-700 hover:bg-gray-200',
                'focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className={clsx(
                'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md',
                'bg-purple-600 text-white hover:bg-purple-700',
                'focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
              data-testid="mark-as-transfer-button"
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Marking...
                </>
              ) : (
                <>
                  <ArrowRight size={16} />
                  Mark as Transfer
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default MarkAsTransferModal;
